import { FC } from "react";
import { Trans } from "react-i18next";
import { useTranslation } from "react-i18next";

import { HELP_CENTER } from "constants/shared";
import AuthLayout from "layouts/AuthLayout";

import { Button, FullSectionLoader, Pagination, SearchInput, Text, Title } from "uikit";

import { Footer, FooterLinkExternal } from "../_shared/components/styles";
import { CompanyListItem } from "./components";
import { useSelectCompanyPage } from "./useSelectCompanyPage";
import {
  AddNewCompanyButtonIcon,
  CompaniesList,
  CompaniesListSection,
  Container,
  Content,
  Header,
  PaginationContainer,
} from "./styles";

const SelectCompanyPage: FC = () => {
  const { t } = useTranslation();
  const translationPrefix = `auth_pages.shared.select_company_page`;
  const { metadata, pageData, actions } = useSelectCompanyPage();

  const { isPageLoading, isListLoading } = metadata;
  const { companiesList, searchInputValue } = pageData;
  const {
    handleSearchInputChange,
    handlePageChange,
    handleCreateNewCompanyClick,
    handleCompanyClick,
  } = actions;

  return (
    <AuthLayout isLoading={isPageLoading} showLogo>
      <Container>
        <Header>
          <Title $fs="h24" $align="center" $mb={"8px"}>
            <Trans i18nKey={`${translationPrefix}.title`} />
          </Title>

          <Text $fs="t14" $variant="secondary" $align="center">
            <Trans i18nKey={`${translationPrefix}.description`} />
          </Text>
        </Header>

        <Content>
          <SearchInput
            value={searchInputValue}
            onChange={handleSearchInputChange}
            placeholder={t(`${translationPrefix}.search_input_placeholder`)}
          />

          <CompaniesListSection>
            <CompaniesList>
              {isListLoading && <FullSectionLoader />}
              {!companiesList.total ? (
                <Text $fs="t14" $variant="secondary" $align="center" $mt={"24px"}>
                  <Trans i18nKey={`${translationPrefix}.empty_state_message`} />
                </Text>
              ) : (
                companiesList?.companies.map((company) => (
                  <CompanyListItem
                    key={company.companyId}
                    data={company}
                    onClick={() => handleCompanyClick(company.companyId)}
                    dataTestId={`company-list-item-${company.companyId}`}
                  />
                ))
              )}
            </CompaniesList>
            <PaginationContainer>
              <Pagination
                page={companiesList?.page}
                limit={companiesList?.perPage}
                onPageChange={handlePageChange}
                totalCount={companiesList?.total}
              />
            </PaginationContainer>
          </CompaniesListSection>

          <Button
            $variant="ghost"
            $mb={"24px"}
            $isParentWidth
            onClick={handleCreateNewCompanyClick}
            data-testid="create-new-company-button"
          >
            <AddNewCompanyButtonIcon />
            <Trans i18nKey={`${translationPrefix}.create_new_company_btn`} />
          </Button>
        </Content>

        <Footer>
          <FooterLinkExternal href={HELP_CENTER} target="_blank">
            <Trans i18nKey={`buttons.need_help_btn`} />
          </FooterLinkExternal>
        </Footer>
      </Container>
    </AuthLayout>
  );
};

export default SelectCompanyPage;
